import {
  DEAL_OPERATIONS,
  IMPORT_FIELDS,
  IMPORT_KINDS,
  IMPORT_LEGAL_BASES,
  IMPORT_ROW_STATUSES,
  formatDateTime,
  type ImportKind,
  type ImportLegalBasis,
  type ImportRowStatus,
} from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Download, Play, Undo2 } from 'lucide-react';
import { useId, useState } from 'react';
import { Link, useParams } from 'react-router';

import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button, buttonVariants } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Combobox } from '../../components/ui/combobox.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, errorMessage } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import { useAssignees, useChannels, useDistrictSearch } from '../../lib/contacts-api.ts';
import {
  IMPORT_STATUSES,
  importKeys,
  problemsUrl,
  useImport,
  useImportRows,
  type ImportRow,
  type ImportView,
} from '../../lib/imports-api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { NotFoundPage } from '../status-pages.tsx';
import { STATUS_TONES } from './list.tsx';

const LEGAL_HELP: Record<ImportLegalBasis, string> = {
  prior_relationship:
    'Son o fueron clientes: ya hubo trato con ellos (compraron, vendieron, alquilaron o pidieron información).',
  legitimate_interest:
    'Contactos que la inmobiliaria obtuvo en su actividad normal y a quienes es razonable atender.',
};

/** One import, step by step: mapping → preview → import (with progress) → report. */
export function ImportPage() {
  const { id = '' } = useParams();
  const current = useImport(id);
  const { data: session } = useSession();
  const runner = useRunner(id);
  if (current.isPending) return <Loading />;
  if (current.error) {
    return (current.error as { status?: number }).status === 404 ? (
      <NotFoundPage />
    ) : (
      <Alert tone="danger">{errorMessage(current.error)}</Alert>
    );
  }
  const view = current.data;
  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <Link to="/importaciones" className="text-sm text-primary hover:underline">
          ← Importar datos
        </Link>
        <h1 className="text-2xl font-semibold">
          Importación de {IMPORT_KINDS[view.kind].toLowerCase()}
          {view.format === 'reconnect' ? ' de RECONNECT' : ''}
        </h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge tone={STATUS_TONES[view.status]}>{IMPORT_STATUSES[view.status]}</Badge>
          {view.totalRows} filas · {view.createdBy} ·{' '}
          {formatDateTime(view.createdAt, session?.tenant.timezone)}
        </p>
      </div>
      {runner.running || view.status === 'importing' ? (
        <RunCard view={view} runner={runner} />
      ) : view.status === 'uploaded' || view.status === 'analyzed' ? (
        <>
          <MappingCard view={view} />
          {view.status === 'analyzed' && (
            <PreviewCard view={view} onRun={() => void runner.run()} />
          )}
        </>
      ) : (
        <ReportCard view={view} />
      )}
    </div>
  );
}

function useSetView() {
  const client = useQueryClient();
  return async (view: ImportView) => {
    client.setQueryData(importKeys.detail(view.id), view);
    await client.invalidateQueries({ queryKey: ['imports', 'rows', view.id] });
    await client.invalidateQueries({ queryKey: importKeys.list });
  };
}

// ---------------------------------------------------------------------------
// Mapping and options
// ---------------------------------------------------------------------------

function MappingCard({ view }: { view: ImportView }) {
  const setView = useSetView();
  const { data: session } = useSession();
  const assignees = useAssignees();
  const channels = useChannels();
  const reconnect = view.format === 'reconnect';
  const fields = Object.entries(IMPORT_FIELDS[view.kind]);
  const [mapping, setMapping] = useState<Record<string, number>>(
    view.mapping ?? view.suggestedMapping ?? {},
  );
  const [legalBasis, setLegalBasis] = useState<ImportLegalBasis | ''>(
    view.options?.legalBasis ?? '',
  );
  const [assignee, setAssignee] = useState(view.options?.defaultAssigneeId ?? '');
  const [channelId, setChannelId] = useState(view.options?.channelId ?? '');
  const [createDeals, setCreateDeals] = useState(view.options?.createDeals ?? false);
  const [dealOperation, setDealOperation] = useState<'purchase' | 'rent' | 'listing'>(
    view.options?.dealOperation ?? 'purchase',
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const example = (column: number | undefined) =>
    column === undefined
      ? ''
      : ((view.sample ?? []).map((r) => r[column] ?? '').find((v) => v !== '') ?? '');

  const analyze = async () => {
    if (!legalBasis && !reconnect) {
      setError('Elige la base legal de los datos que importas.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const analyzed = await api<ImportView>(`/imports/${view.id}/analyze`, {
        method: 'POST',
        body: {
          mapping: reconnect ? {} : mapping,
          options: {
            ...(legalBasis && !reconnect ? { legalBasis } : {}),
            ...(assignee ? { defaultAssigneeId: assignee } : {}),
            ...(view.kind === 'contacts' && channelId ? { channelId } : {}),
            createDeals: view.kind === 'contacts' && createDeals,
            dealOperation,
          },
        },
      });
      await setView(analyzed);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="space-y-5">
      {reconnect ? (
        <Alert tone="info" title="Formato RECONNECT (RE/MAX)">
          Reconocimos el Excel que exporta RECONNECT: las columnas se asignan solas. Se importan el
          estado (las vendidas, alquiladas y retiradas quedan como historial, con su precio y fecha
          de cierre), los precios, las fechas del acuerdo, las características, la dirección y las
          coordenadas (cifradas), el agente y la foto principal. No trae propietarios: las
          propiedades quedan «Sin propietario» y no se publican hasta que se lo agregues.
        </Alert>
      ) : (
        <>
          <div>
            <h2 className="font-semibold">1. Columnas del archivo</h2>
            <p className="text-sm text-muted-foreground">
              Propusimos la columna de cada campo por su título. Revisa y corrige; lo que no asignes
              no se importa. Los campos con * son obligatorios.
            </p>
          </div>
          <ul className="divide-y divide-border">
            {fields.map(([key, field]) => (
              <li
                key={key}
                className="grid gap-2 py-2 sm:grid-cols-[12rem_minmax(0,1fr)_minmax(0,1fr)] sm:items-center"
              >
                <label htmlFor={`map-${key}`} className="text-sm font-medium">
                  {field.label}
                  {field.required && ' *'}
                </label>
                <Select
                  id={`map-${key}`}
                  value={mapping[key] === undefined ? '' : String(mapping[key])}
                  onChange={(e) => {
                    const column = e.target.value === '' ? null : Number(e.target.value);
                    // One column, one field: whoever had this column loses it.
                    const next = Object.fromEntries(
                      Object.entries(mapping).filter(([k, v]) => k !== key && v !== column),
                    );
                    if (column !== null) next[key] = column;
                    setMapping(next);
                  }}
                >
                  <option value="">— No importar —</option>
                  {view.headers.map((h, i) => (
                    <option key={`${h}-${String(i)}`} value={i}>
                      {h}
                    </option>
                  ))}
                </Select>
                <span className="truncate text-sm text-muted-foreground">
                  {mapping[key] !== undefined &&
                    example(mapping[key]) &&
                    `Ej.: ${example(mapping[key])}`}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="space-y-3">
        <h2 className="font-semibold">{reconnect ? '1' : '2'}. Opciones</h2>
        {!reconnect && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Base legal de estos datos *</legend>
            {(Object.keys(IMPORT_LEGAL_BASES) as ImportLegalBasis[]).map((basis) => (
              <label key={basis} className="flex min-h-11 items-start gap-3 text-sm">
                <input
                  type="radio"
                  name="legal-basis"
                  className="mt-0.5 size-5"
                  checked={legalBasis === basis}
                  onChange={() => {
                    setLegalBasis(basis);
                  }}
                />
                <span>
                  <span className="font-medium">{IMPORT_LEGAL_BASES[basis]}</span>
                  <span className="block text-muted-foreground">{LEGAL_HELP[basis]}</span>
                </span>
              </label>
            ))}
            <p className="text-sm text-muted-foreground">
              Se registra para gestionar la relación comercial, nunca como consentimiento de
              marketing. Cada contacto queda «pendiente de aviso de privacidad» hasta que se lo
              entregues.
            </p>
          </fieldset>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={
              reconnect
                ? 'Agente para las filas cuyo agente no es usuario'
                : 'Agente para las filas sin agente'
            }
          >
            {(a11y) => (
              <Select
                {...a11y}
                value={assignee}
                onChange={(e) => {
                  setAssignee(e.target.value);
                }}
              >
                <option value="">Yo ({session?.user.fullName ?? ''})</option>
                {(assignees.data ?? [])
                  .filter((a) => a.membershipId !== session?.membership.id)
                  .map((a) => (
                    <option key={a.membershipId} value={a.membershipId}>
                      {a.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          {view.kind === 'contacts' && (
            <Field label="Canal para las filas sin canal">
              {(a11y) => (
                <Select
                  {...a11y}
                  value={channelId}
                  onChange={(e) => {
                    setChannelId(e.target.value);
                  }}
                >
                  <option value="">Ninguno</option>
                  {(channels.data ?? [])
                    .filter((c) => c.active)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </Select>
              )}
            </Field>
          )}
        </div>
        {view.kind === 'contacts' && (
          <div className="space-y-2">
            <label className="flex min-h-11 items-start gap-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-5"
                checked={createDeals}
                onChange={(e) => {
                  setCreateDeals(e.target.checked);
                }}
              />
              <span>
                <span className="font-medium">Crear también un negocio por contacto</span>
                <span className="block text-muted-foreground">
                  En la primera etapa del embudo predeterminado. Útil si el archivo son leads
                  activos.
                </span>
              </span>
            </label>
            {createDeals && (
              <div className="sm:w-64">
                <Field
                  label="Operación del negocio"
                  hint="Si el archivo tiene la columna «Operación del negocio», se usa esa."
                >
                  {(a11y) => (
                    <Select
                      {...a11y}
                      value={dealOperation}
                      onChange={(e) => {
                        setDealOperation(e.target.value as 'purchase' | 'rent' | 'listing');
                      }}
                    >
                      {Object.entries(DEAL_OPERATIONS).map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
            )}
          </div>
        )}
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <CancelButton view={view} />
        <Button
          variant={view.status === 'analyzed' ? 'secondary' : 'brand'}
          loading={saving}
          onClick={() => void analyze()}
        >
          {view.status === 'analyzed' ? 'Revisar de nuevo' : 'Revisar las filas'}
        </Button>
      </div>
    </Card>
  );
}

function CancelButton({ view }: { view: ImportView }) {
  const setView = useSetView();
  const notify = useToast();
  return (
    <Button
      variant="ghost"
      onClick={() =>
        void api<ImportView>(`/imports/${view.id}/cancel`, { method: 'POST' })
          .then(async (cancelled) => {
            await setView(cancelled);
            notify('Importación cancelada: no se creó nada.', 'success');
          })
          .catch((e: unknown) => {
            notify(errorMessage(e), 'error');
          })
      }
    >
      Cancelar importación
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Preview
// ---------------------------------------------------------------------------

const SUMMARY: {
  status: ImportRowStatus;
  label: string;
  tone: 'success' | 'warning' | 'danger';
}[] = [
  { status: 'valid', label: 'Listas para importar', tone: 'success' },
  { status: 'duplicate', label: 'Duplicadas (se omiten)', tone: 'warning' },
  { status: 'error', label: 'Con errores (se omiten)', tone: 'danger' },
];

function PreviewCard({ view, onRun }: { view: ImportView; onRun: () => void }) {
  const [status, setStatus] = useState<ImportRowStatus | undefined>(undefined);
  const valid = view.counts.valid;
  return (
    <Card className="space-y-4">
      <h2 className="font-semibold">{view.format === 'reconnect' ? '2' : '3'}. Vista previa</h2>
      {view.districtChoices && view.districtChoices.length > 0 && (
        <DistrictChoices view={view} choices={view.districtChoices} />
      )}
      {view.unknownAgents && view.unknownAgents.length > 0 && (
        <Alert tone="warning" title="Agentes que no son usuarios de la inmobiliaria">
          Sus propiedades quedarán a nombre del agente elegido arriba:{' '}
          {view.unknownAgents
            .map((a) => `${a.name} (${String(a.rows)} ${a.rows === 1 ? 'fila' : 'filas'})`)
            .join(', ')}
          . Si quieres que queden a su nombre,{' '}
          <Link to="/admin/usuarios" className="underline">
            invítalos
          </Link>{' '}
          y luego pulsa «Revisar de nuevo».
        </Alert>
      )}
      <div className="grid gap-2 sm:grid-cols-3" role="group" aria-label="Filtrar filas">
        {SUMMARY.map((s) => (
          <button
            key={s.status}
            type="button"
            aria-pressed={status === s.status}
            onClick={() => {
              setStatus(status === s.status ? undefined : s.status);
            }}
            className={cn(
              'rounded-lg border border-border p-3 text-left hover:bg-muted',
              status === s.status && 'border-primary bg-soft/40',
            )}
          >
            <span className="block text-2xl font-semibold tabular-nums">
              {view.counts[s.status]}
            </span>
            <span className="text-sm text-muted-foreground">{s.label}</span>
          </button>
        ))}
      </div>
      <RowList view={view} status={status} />
      {view.counts.duplicate + view.counts.error > 0 && (
        <p className="text-sm text-muted-foreground">
          Las filas omitidas se pueden descargar al final para corregirlas y volver a importarlas.
        </p>
      )}
      <Button variant="brand" disabled={valid === 0} onClick={onRun}>
        <Play /> Importar {valid} {valid === 1 ? 'fila' : 'filas'}
      </Button>
    </Card>
  );
}

/**
 * RECONNECT names that are not a district of the catalog (cantons, ambiguous): one
 * choice per name, remembered for the agency's next imports.
 */
function DistrictChoices({
  view,
  choices,
}: {
  view: ImportView;
  choices: NonNullable<ImportView['districtChoices']>;
}) {
  const setView = useSetView();
  const notify = useToast();
  const [chosen, setChosen] = useState<Record<string, { id: number; label: string }>>({});
  const [saving, setSaving] = useState(false);
  const keyOf = (c: (typeof choices)[number]) => `${c.name}|${c.department ?? ''}`;
  const rows = choices.reduce((n, c) => n + c.rows, 0);
  const save = async () => {
    setSaving(true);
    try {
      const saved = await api<ImportView>(`/imports/${view.id}/districts`, {
        method: 'POST',
        body: {
          choices: choices.flatMap((c) => {
            const pick = chosen[keyOf(c)];
            return pick ? [{ name: c.name, department: c.department, districtId: pick.id }] : [];
          }),
        },
      });
      await setView(saved);
      setChosen({});
      notify(
        'Distritos guardados: la inmobiliaria los recordará en las próximas importaciones.',
        'success',
      );
    } catch (e) {
      notify(errorMessage(e), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <section className="space-y-3 rounded-lg border border-warning/60 bg-warning/10 p-4">
      <div>
        <h3 className="font-medium">
          Elige el distrito de {rows} {rows === 1 ? 'fila' : 'filas'}
        </h3>
        <p className="text-sm text-muted-foreground">
          Estos nombres no son distritos del catálogo (cantones, caseríos o nombres ambiguos). Elige
          uno por nombre: se aplica a todas sus filas y se recuerda para las próximas importaciones.
          Las filas sin distrito no se importan.
        </p>
      </div>
      <ul className="space-y-3">
        {choices.map((c) => (
          <DistrictChoice
            key={keyOf(c)}
            choice={c}
            picked={chosen[keyOf(c)] ?? null}
            onPick={(pick) => {
              setChosen({ ...chosen, [keyOf(c)]: pick });
            }}
          />
        ))}
      </ul>
      <Button
        variant="secondary"
        loading={saving}
        disabled={Object.keys(chosen).length === 0}
        onClick={() => void save()}
      >
        Guardar distritos
      </Button>
    </section>
  );
}

function DistrictChoice({
  choice,
  picked,
  onPick,
}: {
  choice: NonNullable<ImportView['districtChoices']>[number];
  picked: { id: number; label: string } | null;
  onPick: (pick: { id: number; label: string }) => void;
}) {
  const [query, setQuery] = useState('');
  const districts = useDistrictSearch(query);
  const id = useId();
  // The file's department first: a canton is usually in the department RECONNECT says.
  const options = [...(districts.data ?? [])]
    .sort(
      (a, b) =>
        Number(b.department.name === choice.department) -
        Number(a.department.name === choice.department),
    )
    .map((d) => ({
      id: String(d.id),
      label: d.name,
      detail: `${d.municipality.name}, ${d.department.name}`,
    }));
  return (
    <li className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] sm:items-center">
      <label htmlFor={id} className="text-sm">
        <span className="font-medium">{choice.name}</span>
        {choice.department ? ` (${choice.department})` : ''} · {choice.rows}{' '}
        {choice.rows === 1 ? 'fila' : 'filas'}
        {picked && <span className="block text-success">→ {picked.label}</span>}
      </label>
      <Combobox
        id={id}
        query={query}
        onQueryChange={setQuery}
        options={options}
        loading={districts.isFetching}
        onSelect={(option) => {
          setQuery(option.label);
          onPick({ id: Number(option.id), label: `${option.label} (${option.detail ?? ''})` });
        }}
        placeholder="Busca el distrito"
      />
    </li>
  );
}

const MAIN_FIELD: Record<ImportKind | 'reconnect', string[]> = {
  contacts: ['name', 'phone', 'email'],
  properties: ['title', 'district', 'ownerName'],
  reconnect: ['externalId', 'title', 'district'],
};

function RowList({ view, status }: { view: ImportView; status: ImportRowStatus | undefined }) {
  const [page, setPage] = useState(1);
  const rows = useImportRows(view.id, status, page);
  if (rows.isPending) return <Loading />;
  if (rows.error) return <Alert tone="danger">{errorMessage(rows.error)}</Alert>;
  const pages = Math.max(1, Math.ceil(rows.data.total / 50));
  return (
    <div className="space-y-2">
      <ul className="divide-y divide-border rounded-lg border border-border">
        {rows.data.items.map((r) => (
          <RowItem
            key={r.rowNumber}
            row={r}
            kind={view.kind}
            main={MAIN_FIELD[view.format === 'reconnect' ? 'reconnect' : view.kind]}
          />
        ))}
      </ul>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => {
              setPage(page - 1);
            }}
          >
            Anterior
          </Button>
          <span>
            {page} de {pages}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={page >= pages}
            onClick={() => {
              setPage(page + 1);
            }}
          >
            Siguiente
          </Button>
        </div>
      )}
    </div>
  );
}

const ROW_TONES: Record<ImportRowStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  pending: 'neutral',
  valid: 'success',
  duplicate: 'warning',
  error: 'danger',
  created: 'success',
  failed: 'danger',
  undone: 'neutral',
  kept: 'info',
};

function RowItem({ row, kind, main }: { row: ImportRow; kind: ImportKind; main: string[] }) {
  const summary = main
    .map((f) => row.values?.[f])
    .filter(Boolean)
    .join(' · ');
  return (
    <li className="space-y-1 p-3 text-sm">
      <p className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground">Fila {row.rowNumber}</span>
        <Badge tone={ROW_TONES[row.status]}>{IMPORT_ROW_STATUSES[row.status]}</Badge>
        <span className="font-medium">{summary || '—'}</span>
        {row.contactId && kind === 'contacts' && (
          <Link to={`/contactos/${row.contactId}`} className="text-primary hover:underline">
            Ver contacto
          </Link>
        )}
        {row.propertyId && (
          <Link to={`/propiedades/${row.propertyId}`} className="text-primary hover:underline">
            Ver propiedad
          </Link>
        )}
      </p>
      {row.issues.length > 0 && (
        <ul className="space-y-0.5">
          {row.issues.map((issue, i) => (
            <li
              key={`${issue.code}-${String(i)}`}
              className={issue.warning ? 'text-muted-foreground' : 'text-danger'}
            >
              {issue.warning ? 'Aviso: ' : ''}
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Import in batches
// ---------------------------------------------------------------------------

interface Runner {
  running: boolean;
  error: string | null;
  run: () => Promise<void>;
}

/** Calls "run" until the import is done (lives in the page: the cards change meanwhile). */
function useRunner(id: string): Runner {
  const setView = useSetView();
  const notify = useToast();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      let current: ImportView;
      do {
        current = await api<ImportView>(`/imports/${id}/run`, { method: 'POST' });
        await setView(current);
      } while (current.status === 'importing');
      notify('Importación terminada.', 'success');
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setRunning(false);
    }
  };
  return { running, error, run };
}

/** The progress; an interrupted import can be resumed (nothing is created twice). */
function RunCard({ view, runner }: { view: ImportView; runner: Runner }) {
  const total = view.counts.valid + view.counts.created + view.counts.failed;
  const done = view.counts.created + view.counts.failed;
  return (
    <Card className="space-y-4">
      <h2 className="font-semibold">Importando</h2>
      <div className="space-y-1">
        <label htmlFor="import-progress" className="text-sm">
          {done} de {total} filas
        </label>
        <progress
          id="import-progress"
          className="h-3 w-full accent-primary"
          max={Math.max(total, 1)}
          value={done}
        />
      </div>
      <p className="text-sm text-muted-foreground">
        No cierres esta pantalla mientras avanza. Si se interrumpe, vuelve aquí y continúa: nada se
        crea dos veces.
      </p>
      {runner.error && <Alert tone="danger">{runner.error}</Alert>}
      {!runner.running && (
        <Button variant="brand" onClick={() => void runner.run()}>
          <Play /> Continuar importación
        </Button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Report and undo
// ---------------------------------------------------------------------------

function ReportCard({ view }: { view: ImportView }) {
  const { data: session } = useSession();
  const [undoing, setUndoing] = useState(false);
  const [status, setStatus] = useState<ImportRowStatus | undefined>(undefined);
  const c = view.counts;
  const canUndo =
    view.status === 'done' && view.undoUntil !== null && new Date(view.undoUntil) > new Date();
  const problems = c.duplicate + c.error + c.failed + c.kept;
  const allTiles: { status: ImportRowStatus; label: string }[] = [
    {
      status: 'created',
      label: view.kind === 'contacts' ? 'Contactos creados' : 'Propiedades creadas',
    },
    { status: 'duplicate', label: 'Duplicadas (omitidas)' },
    { status: 'error', label: 'Con errores (omitidas)' },
    { status: 'failed', label: 'No se pudieron crear' },
    { status: 'undone', label: 'Deshechas' },
    { status: 'kept', label: 'No se deshicieron' },
  ];
  const tiles = allTiles.filter((t) => c[t.status] > 0);

  return (
    <Card className="space-y-4">
      <h2 className="font-semibold">Resultado</h2>
      {view.status === 'cancelled' && (
        <Alert tone="info">Se canceló antes de importar: no se creó nada.</Alert>
      )}
      {view.status === 'expired' && (
        <Alert tone="info">Venció sin terminarse; los datos del archivo ya se borraron.</Alert>
      )}
      {tiles.length > 0 && (
        <div className="grid gap-2 sm:grid-cols-3" role="group" aria-label="Filtrar filas">
          {tiles.map((t) => (
            <button
              key={t.status}
              type="button"
              aria-pressed={status === t.status}
              onClick={() => {
                setStatus(status === t.status ? undefined : t.status);
              }}
              className={cn(
                'rounded-lg border border-border p-3 text-left hover:bg-muted',
                status === t.status && 'border-primary bg-soft/40',
              )}
            >
              <span className="block text-2xl font-semibold tabular-nums">{c[t.status]}</span>
              <span className="text-sm text-muted-foreground">{t.label}</span>
            </button>
          ))}
        </div>
      )}
      {view.kind === 'contacts' && c.created > 0 && view.status === 'done' && (
        <Alert tone="info" title="Aviso de privacidad pendiente">
          Los contactos importados quedaron «pendientes de aviso de privacidad». Entrégaselo en el
          próximo contacto y márcalo en su ficha.{' '}
          <Link to="/contactos?noticePending=true" className="text-primary underline">
            Ver los pendientes
          </Link>
        </Alert>
      )}
      <div className="flex flex-wrap gap-2">
        {problems > 0 && view.hasData && (
          <a
            href={problemsUrl(view.id)}
            download
            className={buttonVariants({ variant: 'secondary' })}
          >
            <Download /> Descargar filas a revisar
          </a>
        )}
        {canUndo && (
          <Button
            variant="ghost"
            onClick={() => {
              setUndoing(true);
            }}
          >
            <Undo2 /> Deshacer importación
          </Button>
        )}
      </div>
      {canUndo && view.undoUntil && (
        <p className="text-sm text-muted-foreground">
          Se puede deshacer hasta el {formatDateTime(view.undoUntil, session?.tenant.timezone)}.
        </p>
      )}
      {view.status !== 'cancelled' && view.status !== 'expired' && (
        <RowList view={view} status={status} />
      )}
      {undoing && (
        <UndoDialog
          view={view}
          onClose={() => {
            setUndoing(false);
          }}
        />
      )}
    </Card>
  );
}

function UndoDialog({ view, onClose }: { view: ImportView; onClose: () => void }) {
  const setView = useSetView();
  const client = useQueryClient();
  const notify = useToast();
  const [saving, setSaving] = useState(false);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Deshacer la importación"
      description="Lo que se creó y nadie editó ni usó después va a la papelera (se puede restaurar durante 30 días). Lo demás se conserva y te decimos por qué."
    >
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          loading={saving}
          onClick={() => {
            setSaving(true);
            void api<ImportView>(`/imports/${view.id}/undo`, { method: 'POST' })
              .then(async (undone) => {
                await setView(undone);
                await client.invalidateQueries({ queryKey: ['contacts'] });
                await client.invalidateQueries({ queryKey: ['properties'] });
                await client.invalidateQueries({ queryKey: ['deals'] });
                notify(
                  undone.counts.kept > 0
                    ? `Se deshicieron ${String(undone.counts.undone)}; ${String(undone.counts.kept)} se conservaron.`
                    : 'Importación deshecha.',
                  'success',
                );
                onClose();
              })
              .catch((e: unknown) => {
                notify(errorMessage(e), 'error');
                setSaving(false);
              });
          }}
        >
          Deshacer
        </Button>
      </div>
    </Dialog>
  );
}
